import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function AdminUsers(){
  const [users,setUsers] = useState([]);
  const [unions, setUnions] = useState([]);
  const [editingUnion, setEditingUnion] = useState(null);
  const [showUnionPopup, setShowUnionPopup] = useState(false);
  const [approvingManager, setApprovingManager] = useState(null);
  const [selectedUnion, setSelectedUnion] = useState('');

  useEffect(()=> {
    async function load() {
      const snap = await getDocs(collection(db,'users'));
      setUsers(snap.docs.map(d=>({ id:d.id, ...d.data() })));
      
      // Load unions
      const unionSnap = await getDocs(collection(db, 'unions'));
      setUnions(unionSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    load();
  },[]);

function openApprovalPopup(user) {
  setApprovingManager(user);
  setSelectedUnion(user.union || '');
}

async function approveManager() {
  try {
    if (!selectedUnion) {
      alert('Please select a union for this manager');
      return;
    }

    await updateDoc(doc(db, 'users', approvingManager.id), {
      approved: true,
      role: 'manager',
      union: selectedUnion
    });

    alert('Manager approved and assigned to ' + selectedUnion);
    setApprovingManager(null);
    setSelectedUnion('');

    const snap = await getDocs(collection(db, 'users'));
    setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  } catch (err) {
    console.error(err);
    alert('Approval failed');
  }
}


  async function revoke(uid) {
    await updateDoc(doc(db,'users',uid), { approved: false, role: 'pending' });
    alert('User revoked');
    const snap = await getDocs(collection(db,'users'));
    setUsers(snap.docs.map(d=>({ id:d.id, ...d.data() })));
  }

  // Union management functions
  function openEditUnion(union) {
    setEditingUnion(union || {
      name: '',
      licenseStatus: 'active',
      expirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year from now
      createdAt: new Date()
    });
    setShowUnionPopup(true);
  }

  async function saveUnion() {
    try {
      if (!editingUnion.name) {
        alert('Please enter a union name');
        return;
      }

      const unionData = {
        name: editingUnion.name,
        licenseStatus: editingUnion.licenseStatus,
        expirationDate: editingUnion.expirationDate,
        updatedAt: new Date()
      };

      if (editingUnion.id) {
        // Update existing
        await setDoc(doc(db, 'unions', editingUnion.id), unionData, { merge: true });
      } else {
        // Create new
        unionData.createdAt = new Date();
        await setDoc(doc(db, 'unions', editingUnion.name), unionData);
      }

      alert('Union saved successfully');
      setShowUnionPopup(false);
      
      // Reload data
      const unionSnap = await getDocs(collection(db, 'unions'));
      setUnions(unionSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      alert('Failed to save union: ' + err.message);
    }
  }

  // Get managers for a specific union
  function getUnionManagers(unionName) {
    return users.filter(u => u.union === unionName && u.role === 'manager' && u.approved);
  }

  // Check if license is expired
  function isLicenseExpired(expirationDate) {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  }

  // Filter to show only pending managers
  const pendingManagers = users.filter(u => u.requestedRole === 'manager' && !u.approved);
  const approvedManagers = users.filter(u => u.role === 'manager' && u.approved);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Admin Dashboard</h2>

      {/* UNION MANAGEMENT SECTION */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-blue-700">Union License Management</h3>
          <button
            onClick={() => openEditUnion(null)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            + Add New Union
          </button>
        </div>

        {unions.length === 0 ? (
          <p className="text-gray-500">No unions registered yet.</p>
        ) : (
          <div className="space-y-4">
            {unions.map(union => {
              const managers = getUnionManagers(union.name);
              const isExpired = isLicenseExpired(union.expirationDate);
              const daysUntilExpiry = union.expirationDate 
                ? Math.ceil((new Date(union.expirationDate) - new Date()) / (1000 * 60 * 60 * 24))
                : null;

              return (
                <div key={union.id} className="bg-white p-5 rounded shadow border-l-4 border-blue-600">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold">{union.name}</h4>
                      <div className="mt-2 flex items-center gap-4">
                        <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          isExpired 
                            ? 'bg-red-100 text-red-800' 
                            : union.licenseStatus === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {isExpired ? '❌ EXPIRED' : union.licenseStatus === 'active' ? '✓ ACTIVE' : '⏸ INACTIVE'}
                        </div>
                        <div className="text-sm text-gray-600">
                          Expires: <span className={`font-semibold ${
                            daysUntilExpiry < 30 ? 'text-red-600' : 
                            daysUntilExpiry < 90 ? 'text-yellow-600' : 
                            'text-gray-700'
                          }`}>
                            {union.expirationDate 
                              ? new Date(union.expirationDate).toLocaleDateString()
                              : 'No expiration set'}
                            {daysUntilExpiry !== null && !isExpired && (
                              <span className="ml-2">({daysUntilExpiry} days)</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => openEditUnion(union)}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Edit License
                    </button>
                  </div>

                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Assigned Managers ({managers.length}):
                    </p>
                    {managers.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No managers assigned</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {managers.map(m => (
                          <div key={m.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                            <div className="flex-1">
                              <p className="text-sm font-semibold">{m.fullName}</p>
                              <p className="text-xs text-gray-600">{m.email}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <hr className="my-8" />
      
      <h2 className="text-2xl font-bold mb-6">Referee Manager Approvals</h2>
      
      {/* PENDING MANAGERS */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4 text-yellow-700">Pending Manager Requests</h3>
        {pendingManagers.length === 0 ? (
          <p className="text-gray-500">No pending manager requests.</p>
        ) : (
          <div className="space-y-3">
            {pendingManagers.map(u=>(
              <div key={u.id} className="bg-white p-4 rounded shadow border-l-4 border-yellow-500">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-lg">{u.fullName}</div>
                    <div className="text-sm text-gray-600 mt-1">Email: {u.email}</div>
                    <div className="text-sm text-gray-600">Phone: {u.phoneNumber || 'N/A'}</div>
                    <div className="text-sm font-semibold text-blue-700 mt-2">Union: {u.union || 'N/A'}</div>
                  </div>
                  <div className="space-x-2">
                    <button 
                      onClick={()=>openApprovalPopup(u)}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Approve as Manager
                    </button>
                    <button 
                      className="px-4 py-2 border border-gray-400 rounded hover:bg-gray-100" 
                      onClick={()=>revoke(u.id)}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* APPROVED MANAGERS */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-green-700">Approved Managers</h3>
        {approvedManagers.length === 0 ? (
          <p className="text-gray-500">No approved managers yet.</p>
        ) : (
          <div className="space-y-3">
            {approvedManagers.map(u=>(
              <div key={u.id} className="bg-white p-4 rounded shadow border-l-4 border-green-600">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{u.fullName} <span className="text-sm text-gray-500">({u.email})</span></div>
                    <div className="text-sm text-gray-600">Union: {u.union || 'N/A'} • Role: Manager</div>
                  </div>
                  <button className="px-3 py-1 border rounded hover:bg-gray-100" onClick={()=>revoke(u.id)}>Revoke</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MANAGER APPROVAL POPUP */}
      {approvingManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Approve Manager</h3>

            <div className="mb-4 p-4 bg-gray-50 rounded">
              <p className="font-semibold">{approvingManager.fullName}</p>
              <p className="text-sm text-gray-600">{approvingManager.email}</p>
              <p className="text-sm text-gray-600 mt-1">Current Union: {approvingManager.union || 'Not set'}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Assign to Union *</label>
                <select
                  value={selectedUnion}
                  onChange={(e) => setSelectedUnion(e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Union</option>
                  {unions
                    .filter(u => u.licenseStatus === 'active' && (!u.expirationDate || new Date(u.expirationDate) >= new Date()))
                    .map(u => (
                      <option key={u.id} value={u.name}>{u.name}</option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Only active, licensed unions are shown
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={approveManager}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded font-semibold hover:bg-green-700"
              >
                Approve & Assign
              </button>
              <button
                onClick={() => {
                  setApprovingManager(null);
                  setSelectedUnion('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded font-semibold hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UNION EDIT POPUP */}
      {showUnionPopup && editingUnion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">
              {editingUnion.id ? 'Edit Union License' : 'Add New Union'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Union Name *</label>
                <input
                  type="text"
                  value={editingUnion.name}
                  onChange={(e) => setEditingUnion({...editingUnion, name: e.target.value})}
                  placeholder="e.g., Freestate Rugby Referee"
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!!editingUnion.id} // Can't change name of existing union
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">License Status</label>
                <select
                  value={editingUnion.licenseStatus}
                  onChange={(e) => setEditingUnion({...editingUnion, licenseStatus: e.target.value})}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Expiration Date *</label>
                <input
                  type="date"
                  value={editingUnion.expirationDate}
                  onChange={(e) => setEditingUnion({...editingUnion, expirationDate: e.target.value})}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  System will automatically mark license as expired after this date
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={saveUnion}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded font-semibold hover:bg-blue-700"
              >
                Save Union
              </button>
              <button
                onClick={() => setShowUnionPopup(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded font-semibold hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
